import type { UIRouterReact } from "@uirouter/react";

import { swSettingsReady } from "app/actions/commonActions";
import { startImpersonation } from "app/actions/impersonateActions";
import { setAllStatesCompareStatusAction, setUserCompareStatusAction } from "app/actions/userData/userEngagementActions";
import { chosenSites } from "app/common/services/chosenSites";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { getAllStatesCompareStatus, getUserCompareStatue } from "app/components/compare/QueryBarUtils";
import { ApiManagementService } from "app/pages/account/api-management/apiManagementService";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { routerOnBefore } from "single-spa/react-app/router/router-on-before";
import { CIG_UPDATE_LINK_WITH_TOKEN, getCigLinkWithToken } from "app/services/CIGService";
import { SwRedux } from "app/services/SwRedux";
import { SwTrack } from "app/services/SwTrack";
import { mixpanelTracker } from "app/services/track/track";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore

import { statesConfig } from "../config/statesConfig";
import { proNavigator } from "../ProNavigator";
import { routerOnError } from "../router/router-on-error";
import { routerOnStart } from "../router/router-on-start";
import { routerOnSuccess } from "../router/router-on-success";
import { routerOtherwiseRules } from "../router/router-otherwise-rules";

declare const window: any;
export const appInit = async (router: UIRouterReact) => {
    // Register all states and nav handlers
    registerStates(router);
    routerOtherwiseRules(router);
    routerOnStart(router);
    routerOnError(router);
    routerOnSuccess(router);
    routerOnBefore(router);

    const swRedux = SwRedux.getInstance();
    const SIXTY_MINUTES = 60 * 60 * 1000; // in ms
    // dispatch an event with swSettings so settings will be available in redux reducers
    setTimeout(() => {
        if (!swRedux.getState()) {
            return; // Prevent dispatch if store is not ready
        }

        swRedux.dispatch(swSettingsReady(swSettings));

        if (swSettings.components.CigReportGenerator.resources.AvaliabilityMode !== EAvailabilityMode.OPEN) {
            swRedux.dispatch({
                type: CIG_UPDATE_LINK_WITH_TOKEN,
                cigLink: "/#/tools/cig",
            });
        } else {
            // Get cig full link based on available API token. Refresh every 60 minutes.
            swRedux.dispatch(getCigLinkWithToken(new ApiManagementService()));
            setInterval(() => {
                if (!document.hidden) {
                    // Check that tab is active. Not reason to refresh token if not
                    swRedux.dispatch(getCigLinkWithToken(new ApiManagementService()));
                }
            }, SIXTY_MINUTES);
        }
    }, 100);

    SwTrack.init();

    // track healthChecks timeout
    setTimeout(() => {
        SwTrack.healthChecks();
    }, 50000);

    // this section is for handling tracking from mobile view page, which happen before mixpanel is ready and the mobile page already closed
    if (window.mobileViewPage && window.mobileViewPage.length > 0) {
        (window.mobileViewPage as any[]).forEach((item) => {
            if (item.event === "trackEvent") {
                mixpanelTracker.trackEvent(item.params[0], item.params[1], item.params[2]);
            } else if (item.event === "trackPageView") {
                mixpanelTracker.trackPageView(item.params);
            }
        });
        delete window.mobileViewPage;
    }
    // RIPNG: im not sure this is the best place for this code
    setTimeout(() => {
        const swRedux = SwRedux.getInstance();
        if (!swRedux.getState()) return; // Prevent dispatch if store is not ready

        getUserCompareStatue().then((userCompareStatus) => {
            swRedux.dispatch(setUserCompareStatusAction(userCompareStatus));
        });
        getAllStatesCompareStatus().then((allStatesCompareStatus) => {
            swRedux.dispatch(setAllStatesCompareStatusAction(allStatesCompareStatus));
        });
        if (swSettings.components.Home.resources.Impersonates) {
            let showImpersonationTooltip;
            try {
                showImpersonationTooltip = JSON.parse(window.localStorage.getItem("impersonationRequested"));
                if (showImpersonationTooltip) {
                    window.localStorage.removeItem("impersonationRequested");
                }
            } catch (e) {}
            swRedux.dispatch(startImpersonation(showImpersonationTooltip));
        }
    });

    proNavigator.init(router);
    chosenSites.init(router);
    // stateService.setUpDefaults();
    // loadGtm();
};

// Workaround - change the type of the parameter in order to prevent the router from encoding it
const fixStateParameter = (state: any, paramName: any) => {
    if (state.url && state.url.includes(`:${paramName}/`)) {
        if (!state.params) {
            state.params = {};
        }
        if (state.params[paramName]) {
            state.params[paramName].raw = true;
        } else {
            state.params[paramName] = { type: "string", raw: true };
        }
    }
};

const registerStates = (router: UIRouterReact) => {
    Object.entries<IRouterState>(statesConfig as any).forEach(([name, state]) => {
        // some states doesnt have 'name' property, so we need the property name instead
        (state as any).name = name;
        fixStateParameter(state, "keyword");
        fixStateParameter(state, "key");
        fixStateParameter(state, "appId");

        router.stateRegistry.register(state);
    });
};
